// src/pages/relapse/AddRelapsePage.tsx - Add/edit relapse incident page

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import { 
  createRelapseIncident, 
  updateRelapseIncident,
  fetchTriggerTags
} from '../../store/slices/relapseSlice';
import { AppDispatch, RootState } from '../../store';
import { useAuth } from '../../contexts/AuthContext';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const relapseTypes = [
  'Substance Use',
  'Anger/Rage',
  'Self-Sabotage',
  'Procrastination',
  'Negative Self-Talk',
  'Avoidance',
  'Impulsive Behavior',
  'Other'
];

const AddRelapsePage: React.FC = () => {
  const { incidentId } = useParams<{ incidentId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { currentUser } = useAuth();
  
  const { incidents, triggers, isLoading, error } = useSelector((state: RootState) => state.relapse);
  
  const [type, setType] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedTriggers, setSelectedTriggers] = useState<string[]>([]);
  const [customType, setCustomType] = useState('');
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  
  useEffect(() => {
    if (currentUser) {
      dispatch(fetchTriggerTags(currentUser.uid));
      
      // If editing existing incident
      if (incidentId) {
        const incident = incidents.find(inc => inc.id === incidentId);
        
        if (incident) {
          setType(incident.type);
          setDescription(incident.description);
          setNotes(incident.notes || '');
          setSelectedTriggers(incident.triggers || []);
          
          if (!relapseTypes.includes(incident.type)) {
            setCustomType(incident.type);
          }
        }
      }
    }
  }, [currentUser, incidentId, incidents, dispatch]);
  
  const handleTypeChange = (event: any) => {
    setType(event.target.value);
  };
  
  const handleTriggerChange = (event: any) => {
    setSelectedTriggers(event.target.value as string[]);
  };
  
  const handleSave = async () => {
    if (!currentUser || !type || !description.trim()) return;
    
    const finalType = type === 'Other' ? customType : type;
    
    if (type === 'Other' && !customType.trim()) {
      return;
    }
    
    const incidentData = {
      userId: currentUser.uid,
      type: finalType,
      description: description.trim(),
      notes: notes.trim(),
      triggers: selectedTriggers,
      timestamp: Date.now(),
    };
    
    let result;
    
    if (incidentId) {
      // Update existing incident
      result = await dispatch(updateRelapseIncident({
        userId: currentUser.uid,
        incidentId,
        updates: incidentData
      }));
      
      if (updateRelapseIncident.fulfilled.match(result)) {
        navigate('/relapse');
      }
    } else {
      // Create new incident
      result = await dispatch(createRelapseIncident(incidentData));
      
      if (createRelapseIncident.fulfilled.match(result)) {
        navigate('/relapse');
      }
    }
  };
  
  const handleCancel = () => {
    if (
      type || 
      description.trim() || 
      notes.trim() || 
      selectedTriggers.length > 0 || 
      customType.trim()
    ) {
      setDiscardDialogOpen(true);
    } else {
      navigate('/relapse');
    }
  };
  
  const handleDiscard = () => {
    setDiscardDialogOpen(false);
    navigate('/relapse');
  };
  
  const handleKeepEditing = () => {
    setDiscardDialogOpen(false);
  };
  
  return (
    <Container component="main" maxWidth="md">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography component="h1" variant="h4" sx={{ mb: 4 }}>
          {incidentId ? 'Edit Incident' : 'Record Incident'}
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="type-label">Type of Relapse</InputLabel>
            <Select
              labelId="type-label"
              id="type"
              value={type}
              label="Type of Relapse"
              onChange={handleTypeChange}
              disabled={isLoading}
            >
              <MenuItem value="">
                <em>Select type</em>
              </MenuItem>
              {relapseTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {type === 'Other' && (
            <TextField
              fullWidth
              id="customType"
              label="Specify Type"
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              disabled={isLoading}
              sx={{ mb: 3 }}
            />
          )}
          
          <TextField
            fullWidth
            id="description"
            label="Description"
            multiline
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe what happened..."
            disabled={isLoading}
            sx={{ mb: 3 }}
          />
          
          <TextField
            fullWidth
            id="notes"
            label="Additional Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any additional context, thoughts, or reflections..."
            disabled={isLoading}
            sx={{ mb: 3 }}
          />
          
          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="triggers-label">Triggers</InputLabel>
            <Select
              labelId="triggers-label"
              id="triggers"
              multiple
              value={selectedTriggers}
              onChange={handleTriggerChange}
              input={<OutlinedInput id="select-multiple-chip" label="Triggers" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {(selected as string[]).map((triggerId) => {
                    const trigger = triggers.find(t => t.id === triggerId);
                    return (
                      <Chip 
                        key={triggerId} 
                        label={trigger?.name || 'Unknown'} 
                        sx={{ 
                          backgroundColor: trigger?.color || '#3F51B5',
                          color: 'white'
                        }}
                      />
                    );
                  })}
                </Box>
              )}
              MenuProps={MenuProps}
              disabled={isLoading || triggers.length === 0}
            >
              {triggers.length === 0 ? (
                <MenuItem disabled>
                  <em>No triggers defined</em>
                </MenuItem>
              ) : (
                triggers.map((trigger) => (
                  <MenuItem
                    key={trigger.id}
                    value={trigger.id}
                  >
                    <Box 
                      sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        backgroundColor: trigger.color || '#3F51B5',
                        mr: 1
                      }} 
                    />
                    {trigger.name}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          {triggers.length === 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              No trigger tags defined. You can create them in the Manage Triggers section.
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
            
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                isLoading || 
                !type || 
                !description.trim() || 
                (type === 'Other' && !customType.trim())
              }
            >
              {isLoading ? <CircularProgress size={24} /> : 'Save'}
            </Button>
          </Box>
        </Paper>
      </Box>
      
      {/* Discard Changes Dialog */}
      <Dialog
        open={discardDialogOpen}
        onClose={handleKeepEditing}
      >
        <DialogTitle>Discard Changes?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Are you sure you want to discard them?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepEditing}>Keep Editing</Button>
          <Button onClick={handleDiscard} color="error">Discard</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AddRelapsePage;
